<template>
  <div class="badge statusPill" :data-status="status">
    {{ label || status }}
  </div>
</template>

<script setup>
const props = defineProps({
  status: { type: String, required: true },
  label: { type: String, default: '' },
})
</script>

<style scoped>
.statusPill {
  white-space: nowrap;
}

/* Neutral statuses */
.statusPill[data-status="pending"],
.statusPill[data-status="open"],
.statusPill[data-status="pending_approval"],
.statusPill[data-status="sent"],
.statusPill[data-status="deleted"],
.statusPill[data-status="queued"],
.statusPill[data-status="ignored"],
.statusPill[data-status="skipped"] {
  background: var(--status-neutral-bg);
  border-color: var(--status-neutral-border);
  color: var(--status-neutral-text);
}

.statusPill[data-status="cancelled"],
.statusPill[data-status="rejected"] {
  background: var(--status-neutral-bg);
  border-color: var(--status-neutral-border);
  color: var(--status-neutral-text);
  opacity: 0.7;
}

/* Success statuses */
.statusPill[data-status="done"],
.statusPill[data-status="delivered"],
.statusPill[data-status="completed"],
.statusPill[data-status="applied"] {
  background: var(--status-success-bg);
  border-color: var(--status-success-border);
  color: var(--status-success-text);
}

/* Warning statuses */
.statusPill[data-status="sending"],
.statusPill[data-status="bounced"],
.statusPill[data-status="processing"] {
  background: var(--status-warning-bg);
  border-color: var(--status-warning-border);
  color: var(--status-warning-text);
}

/* Danger/Error statuses */
.statusPill[data-status="error"],
.statusPill[data-status="failed"],
.statusPill[data-status="spam"],
.statusPill[data-status="send_failed"] {
  background: var(--status-danger-bg);
  border-color: var(--status-danger-border);
  color: var(--status-danger-text);
}

/* Info/Running statuses */
.statusPill[data-status="running"],
.statusPill[data-status="queued_for_sending"],
.statusPill[data-status="opened"] {
  background: rgba(59, 130, 246, 0.14);
  border-color: rgba(59, 130, 246, 0.35);
  color: #1e40af;
}

html[data-theme="dark"] .statusPill[data-status="running"],
html[data-theme="dark"] .statusPill[data-status="queued_for_sending"],
html[data-theme="dark"] .statusPill[data-status="opened"] {
  background: rgba(59, 130, 246, 0.12);
  border-color: rgba(59, 130, 246, 0.24);
  color: #93c5fd;
}

/* Voicemail terminal — call attempted, no human conversation */
.statusPill[data-status="voicemail_left"] {
  background: var(--status-success-bg);
  border-color: var(--status-success-border);
  color: var(--status-success-text);
}
.statusPill[data-status="no_answer"],
.statusPill[data-status="busy"],
.statusPill[data-status="out_of_window"],
.statusPill[data-status="out_of_capacity"] {
  background: var(--status-neutral-bg, rgba(148, 163, 184, 0.14));
  border-color: var(--status-neutral-border, rgba(148, 163, 184, 0.3));
  color: var(--status-neutral-text, #475569);
}

/* Voicemail in-flight — info/blue, matches queued_for_sending shade */
.statusPill[data-status="passed_to_agent_dialer"],
.statusPill[data-status="scheduled"],
.statusPill[data-status="retry_pending"] {
  background: rgba(59, 130, 246, 0.14);
  border-color: rgba(59, 130, 246, 0.35);
  color: #1e40af;
}
html[data-theme="dark"] .statusPill[data-status="passed_to_agent_dialer"],
html[data-theme="dark"] .statusPill[data-status="scheduled"],
html[data-theme="dark"] .statusPill[data-status="retry_pending"] {
  background: rgba(59, 130, 246, 0.12);
  border-color: rgba(59, 130, 246, 0.24);
  color: #93c5fd;
}

/* Voicemail in-flight — dialing (pulsing blue dot prefix) */
.statusPill[data-status="dialing"] {
  background: rgba(59, 130, 246, 0.18);
  border-color: rgba(59, 130, 246, 0.45);
  color: #1e40af;
}
.statusPill[data-status="dialing"]::before {
  content: "";
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #3b82f6;
  margin-right: 6px;
  vertical-align: middle;
  animation: dialingPulse 1.2s ease-in-out infinite;
}
html[data-theme="dark"] .statusPill[data-status="dialing"] {
  background: rgba(59, 130, 246, 0.16);
  border-color: rgba(59, 130, 246, 0.32);
  color: #93c5fd;
}
@keyframes dialingPulse {
  0%, 100% { opacity: 0.4; transform: scale(0.85); }
  50%      { opacity: 1.0; transform: scale(1.15); }
}

/* Handoff intermediate — distinct purple */
.statusPill[data-status="passed_to_human_dialer"] {
  background: rgba(139, 92, 246, 0.14);
  border-color: rgba(139, 92, 246, 0.35);
  color: #6d28d9;
}
html[data-theme="dark"] .statusPill[data-status="passed_to_human_dialer"] {
  background: rgba(139, 92, 246, 0.16);
  border-color: rgba(139, 92, 246, 0.3);
  color: #c4b5fd;
}

/* SDR conversation outcomes — success / neutral / danger */
.statusPill[data-status="connected_interested"],
.statusPill[data-status="connected_callback"] {
  background: var(--status-success-bg);
  border-color: var(--status-success-border);
  color: var(--status-success-text);
}
.statusPill[data-status="connected_not_interested"],
.statusPill[data-status="gatekeeper"] {
  background: var(--status-neutral-bg, rgba(148, 163, 184, 0.14));
  border-color: var(--status-neutral-border, rgba(148, 163, 184, 0.3));
  color: var(--status-neutral-text, #475569);
}
.statusPill[data-status="wrong_number"],
.statusPill[data-status="do_not_call"],
.statusPill[data-status="dialer_failed"] {
  background: var(--status-danger-bg);
  border-color: var(--status-danger-border);
  color: var(--status-danger-text);
}

/* Inbound callback — warning orange so the user notices */
.statusPill[data-status="call_returned"] {
  background: var(--status-warning-bg);
  border-color: var(--status-warning-border);
  color: var(--status-warning-text);
}

/* Human-dialer membership status (history STATUS column) + a coarse connected pickup.
   `engaged`/`human_picked_up` = success; `active` = info/blue; `disqualified` = neutral;
   `removed_*` = danger. */
.statusPill[data-status="engaged"],
.statusPill[data-status="human_picked_up"] {
  background: var(--status-success-bg);
  border-color: var(--status-success-border);
  color: var(--status-success-text);
}
.statusPill[data-status="active"] {
  background: rgba(59, 130, 246, 0.14);
  border-color: rgba(59, 130, 246, 0.35);
  color: #1e40af;
}
html[data-theme="dark"] .statusPill[data-status="active"] {
  background: rgba(59, 130, 246, 0.12);
  border-color: rgba(59, 130, 246, 0.24);
  color: #93c5fd;
}
.statusPill[data-status="disqualified"] {
  background: var(--status-danger-bg);
  border-color: var(--status-danger-border);
  color: var(--status-danger-text);
}
.statusPill[data-status="removed_wrong_number"],
.statusPill[data-status="removed_number_changed"] {
  background: var(--status-neutral-bg, rgba(148, 163, 184, 0.14));
  border-color: var(--status-neutral-border, rgba(148, 163, 184, 0.3));
  color: var(--status-neutral-text, #475569);
}
</style>
